/**
 * Senyals de la card del calendari (variant L) — `plans/calendar-card-signals.md` §6 F1.1 i F2.4.
 *
 * L'única font de colors, ratllats, símbols i textos de la llegenda. La card (`PaymentStripe`,
 * `CardLights`) i la llegenda (`CalendarLegend`) en beuen totes dues: si un color canvia, canvia aquí.
 *
 * Els `reason` de cada reserva els calcula el backend; aquí només hi ha els textos genèrics de la llegenda.
 */

import type { PaymentStripe, PaymentVia, StayLight, TaskLight } from '@guilleumes/types';

// ============================================
// FRANJA DE PAGAMENT (esquerra)
// ============================================

/** Amplada de la franja de pagament, enganxada a la barra del canal (D1). */
export const PAYMENT_STRIPE_WIDTH = 8;

/** Estats que pinten franja. `NONE` = sense franja (D2: si no hi ha color, no hi ha feina). */
export type VisiblePaymentStripe = Exclude<PaymentStripe, 'NONE'>;

export interface PaymentStripeStyle {
  /** Valor de la propietat CSS `background` (color o degradat). */
  background: string;
  /** Text de la llegenda. */
  label: string;
}

export const PAYMENT_STRIPE_STYLES: Record<VisiblePaymentStripe, PaymentStripeStyle> = {
  PENDING: {
    background: '#F97316',
    label: 'Pendent, però paga en arribar (client de confiança o allargament), o encara falta més d\'una setmana',
  },
  REQUESTED: {
    background: 'repeating-linear-gradient(135deg,#F97316 0 3px,#fff 3px 5px)',
    label: 'Link enviat, transferència demanada o factura emesa a una empresa: esperant el cobrament',
  },
  PARTIAL: {
    background: 'linear-gradient(to top,#F97316 50%,#FED7AA 50%)',
    label: "Ha pagat una part: paga i senyal, o ha allargat l'estada i falta la resta",
  },
  OVERDUE: {
    background: '#B91C1C',
    label: "Ja hauria d'haver pagat: cal cobrar",
  },
  COMPANY: {
    background: 'repeating-linear-gradient(135deg,#64748B 0 3px,#fff 3px 5px)',
    label: 'A facturar a una empresa (sola, amb el grup o amb la factura del mes), també amb l’esborrany encara sense emetre',
  },
  REVIEW: {
    background: '#7C3AED',
    label: 'Revisar a mà: la reserva diu que cobra l\'hotel, o no consta qui cobra',
  },
};

/** Text de la llegenda per a l'absència de franja. */
export const PAYMENT_NONE_LABEL = 'Pagada, o la cobra Booking o Expedia (sense franja)';

/** Ordre de la llegenda. */
export const PAYMENT_STRIPE_ORDER: readonly VisiblePaymentStripe[] = [
  'PENDING',
  'REQUESTED',
  'PARTIAL',
  'OVERDUE',
  'COMPANY',
  'REVIEW',
];

// ============================================
// LLUMS (dreta: dalt entrada/sortida · mig registre · baix contacte)
// ============================================

/**
 * Aspecte d'un llum, comú als tres. Els estats del llum de dalt (`StayLight`) es tradueixen a aquests
 * amb `stayLightKind`: `LEFT` → `OK`, `LATE_OUT` → `WAIT`, `NO_SHOW` → `ACT`.
 */
export type LightKind =
  | 'OFF'
  | 'OK'
  | 'WAIT'
  | 'ACT'
  | 'IN'
  | 'NO_PHONE'
  | 'NO_WHATSAPP'
  | 'NO_WHATSAPP_EARLY'
  | 'ARRIVING_CLEAN'
  | 'ARRIVING_DIRTY';

/**
 * Símbol del llum (D13). La card el pinta amb una icona de lucide o un SVG en línia, no amb emoji,
 * perquè a 10-12 px no es veuen igual. `glyph` és el caràcter equivalent, per a textos i tests.
 */
export type LightSymbol = 'check' | 'dots' | 'alert' | 'dot' | 'phone' | 'whatsapp';

export interface LightStyle {
  /** Fons del quadrat. */
  background: string;
  /** Vora: gruix en px i color. */
  borderWidth: number;
  borderColor: string;
  /** Color del símbol (sempre blanc sobre els plens). */
  color: string;
  symbol: LightSymbol | null;
  glyph: string;
}

export const LIGHT_STYLES: Record<LightKind, LightStyle> = {
  OFF: { background: 'transparent', borderWidth: 1, borderColor: '#E2E8F0', color: 'transparent', symbol: null, glyph: '' },
  OK: { background: '#16A34A', borderWidth: 0, borderColor: 'transparent', color: '#fff', symbol: 'check', glyph: '✓' },
  WAIT: { background: '#D97706', borderWidth: 0, borderColor: 'transparent', color: '#fff', symbol: 'dots', glyph: '…' },
  ACT: { background: '#DC2626', borderWidth: 0, borderColor: 'transparent', color: '#fff', symbol: 'alert', glyph: '!' },
  IN: { background: '#2563EB', borderWidth: 0, borderColor: 'transparent', color: '#fff', symbol: 'dot', glyph: '●' },
  // Sense telèfon, o amb el farciment de l'OTA: el mateix telèfon que «cal trucar», però en vermell (1-OCT-2026).
  NO_PHONE: { background: '#DC2626', borderWidth: 0, borderColor: 'transparent', color: '#fff', symbol: 'phone', glyph: '☎' },
  NO_WHATSAPP: { background: '#64748B', borderWidth: 0, borderColor: 'transparent', color: '#fff', symbol: 'phone', glyph: '☎' },
  // Marcada «No té WhatsApp» i encara lluny: vermell, amb la icona de WhatsApp (1-OCT-2026).
  NO_WHATSAPP_EARLY: { background: '#DC2626', borderWidth: 0, borderColor: 'transparent', color: '#fff', symbol: 'whatsapp', glyph: 'W' },
  ARRIVING_CLEAN: { background: '#fff', borderWidth: 2, borderColor: '#16A34A', color: 'transparent', symbol: null, glyph: '' },
  ARRIVING_DIRTY: { background: '#fff', borderWidth: 2, borderColor: '#EA580C', color: 'transparent', symbol: null, glyph: '' },
};

/** El símbol només es pinta si el llum fa almenys aquests px (§6 F1.3). */
export const LIGHT_SYMBOL_MIN_SIZE = 12;

/** Llum de dalt → aspecte. */
export function stayLightKind(light: StayLight): LightKind {
  switch (light) {
    case 'LEFT':
      return 'OK';
    case 'LATE_OUT':
      return 'WAIT';
    case 'NO_SHOW':
      return 'ACT';
    default:
      return light;
  }
}

/** Llums del mig i de baix → aspecte (són els mateixos noms). */
export function taskLightKind(light: TaskLight): LightKind {
  return light;
}

/**
 * Urgència per a les cards estretes (< 110 px, §6 F1.4): només es pinta el llum més urgent, i «+n» si
 * n'hi ha més d'encesos. Primer el de l'índex 0. `OFF` no compta mai.
 */
export const LIGHT_URGENCY: readonly LightKind[] = [
  'ACT',
  'WAIT',
  'NO_PHONE',
  'NO_WHATSAPP',
  'NO_WHATSAPP_EARLY',
  'ARRIVING_DIRTY',
  'ARRIVING_CLEAN',
  'IN',
  'OK',
];

// ============================================
// LLEGENDA
// ============================================

export interface LegendEntry<K extends string> {
  state: K;
  kind: LightKind;
  label: string;
}

/** Llum de dalt: entrada i sortida. */
export const STAY_LEGEND: readonly LegendEntry<StayLight>[] = [
  { state: 'ARRIVING_CLEAN', kind: 'ARRIVING_CLEAN', label: "Arriba avui i encara no ha entrat; l'habitació està neta i llesta" },
  { state: 'ARRIVING_DIRTY', kind: 'ARRIVING_DIRTY', label: "Arriba avui i encara no ha entrat; l'habitació encara s'ha de netejar" },
  { state: 'IN', kind: 'IN', label: 'Ha entrat: ha obert la porta amb el seu codi o algú ho ha marcat (i fins que marxi)' },
  { state: 'LATE_OUT', kind: 'WAIT', label: 'Surt avui, ja són les 12 h i no consta que hagi marxat' },
  { state: 'LEFT', kind: 'OK', label: 'Ha marxat: checkout fet, cal netejar' },
  { state: 'NO_SHOW', kind: 'ACT', label: "No s'ha presentat (marcat a mà). Si és d'una OTA, marca-ho a l'extranet" },
  { state: 'OFF', kind: 'OFF', label: "Buit: avui no entra, no surt i no és a dins" },
];

/** Llum del mig: registre (el «codi enviat» vindrà amb el WhatsApp connectat). */
export const REGISTRATION_LEGEND: readonly LegendEntry<TaskLight>[] = [
  { state: 'OK', kind: 'OK', label: 'Arriba en 3 dies o menys i el registre és complet' },
  { state: 'WAIT', kind: 'WAIT', label: 'Arriba en 3 dies o menys i el registre és a mitges o sense fer' },
  { state: 'ACT', kind: 'ACT', label: 'Ja és a dins i el registre no està complet (Mossos)' },
  { state: 'OFF', kind: 'OFF', label: 'Buit: no cal mirar-ho ara' },
];

/** Llum de baix: contacte («ha contestat» i «no ha contestat» vindran amb el WhatsApp connectat). */
export const CONTACT_LEGEND: readonly LegendEntry<TaskLight>[] = [
  {
    state: 'NO_PHONE',
    kind: 'NO_PHONE',
    label:
      "No tenim el seu telèfon, o és el número de farciment de l'OTA (11 111 11111): cal recuperar-lo. Surt des que entra la reserva",
  },
  { state: 'NO_WHATSAPP', kind: 'NO_WHATSAPP', label: 'Arriba en 3 dies o menys i no té WhatsApp: cal trucar' },
  {
    state: 'NO_WHATSAPP_EARLY',
    kind: 'NO_WHATSAPP_EARLY',
    label:
      "Marcada «No té WhatsApp» i encara falten més de 3 dies: escriu-li per l'extranet perquè ens contacti. S'apaga amb «Ja li he escrit»",
  },
  { state: 'OFF', kind: 'OFF', label: 'Buit: no cal mirar-ho ara' },
];

// ============================================
// AL COSTAT DEL NOM (línia 1) I VIA (línia 3)
// ============================================

/** Distintiu del grup: fosc a la reserva principal, blanc a la resta (§6 F1.7). */
export const GROUP_BADGE_STYLES = {
  master: { background: '#334155', color: '#fff', borderColor: '#334155', label: "Reserva principal d'un grup" },
  member: { background: '#fff', color: '#334155', borderColor: '#94A3B8', label: 'Del mateix grup' },
} as const;

export const PAY_ON_ARRIVAL_MARK = { glyph: '★', color: '#B45309', label: 'Client de confiança: paga en arribar' } as const;

export const CONTINUES_STAY_MARK = { glyph: '↔', color: '#B45309', label: "Continua l'estada anterior" } as const;

/**
 * 🔒 Bloqueig a l'habitació (`plans/reservation-move-lock.md` §3). Tenen prioritat sobre ★ i ↔ si falta espai.
 * `label` és el text de la llegenda; el `title` de la card porta el motiu o l'avís de cada reserva.
 */
export const MOVE_LOCK_MARK = {
  glyph: '🔒',
  color: '#475569',
  label: 'Bloquejada a aquesta habitació: {motiu}',
} as const;

/** Pastilla vermella «🔒 Moguda»: Beds24 l'ha moguda estant bloquejada. */
export const MOVE_LOCK_ALERT_MARK = {
  glyph: '🔒',
  text: 'Moguda',
  color: '#fff',
  background: '#DC2626',
  label: "Beds24 l'ha moguda estant bloquejada",
} as const;

export interface PaymentViaStyle {
  /** Etiqueta curta de la línia 3. */
  short: string;
  label: string;
}

export const PAYMENT_VIA_STYLES: Record<NonNullable<PaymentVia>, PaymentViaStyle> = {
  OTA_BOOKING: { short: 'B', label: 'La cobra Booking' },
  OTA_EXPEDIA: { short: 'E', label: 'La cobra Expedia' },
  STRIPE: { short: '🔗', label: 'Link de pagament' },
  TRANSFER: { short: '🏦', label: 'Transferència' },
  CARD: { short: '💳', label: 'Targeta' },
  CASH: { short: '💶', label: 'Efectiu' },
  COMPANY: { short: '🏢', label: 'Empresa' },
};

/** Ordre de la llegenda de les vies. */
export const PAYMENT_VIA_ORDER: readonly NonNullable<PaymentVia>[] = [
  'OTA_BOOKING',
  'OTA_EXPEDIA',
  'STRIPE',
  'TRANSFER',
  'CARD',
  'CASH',
  'COMPANY',
];
